export type FavoriteReconcileListing = {
  id: string;
  show_on_web?: boolean | null;
  listing_status?: string | null;
};

export type StaleFavoriteReasons = {
  absent: number;
  hidden: number;
  inactive: number;
};

export type StaleFavoriteComputation = {
  staleIds: string[];
  skippedMassWipe: boolean;
  reasons: StaleFavoriteReasons;
};

/**
 * When false, listing_status is ignored even if the API returns it.
 * The field is readable with the web API key but is currently always null;
 * keep this on so inactive values prune if they start appearing.
 */
export const PRUNE_INACTIVE_LISTING_STATUS = true;

const INACTIVE_LISTING_STATUS = 'inactive';
const MASS_WIPE_MIN_SAVED_IDS = 3;

const emptyReasons = (): StaleFavoriteReasons => ({
  absent: 0,
  hidden: 0,
  inactive: 0,
});

const isInactiveStatus = (status: string | null | undefined) => {
  if (!PRUNE_INACTIVE_LISTING_STATUS) return false;
  if (status == null || status === '') return false;
  return status.trim().toLowerCase() === INACTIVE_LISTING_STATUS;
};

export const computeStaleFavoriteIds = ({
  savedIds,
  returned,
  complete,
  ignoreShowOnWebsite = false,
}: {
  savedIds: string[];
  returned: FavoriteReconcileListing[];
  complete: boolean;
  ignoreShowOnWebsite?: boolean;
}): StaleFavoriteComputation => {
  const empty: StaleFavoriteComputation = {
    staleIds: [],
    skippedMassWipe: false,
    reasons: emptyReasons(),
  };

  if (!complete || savedIds.length === 0) {
    return empty;
  }

  const returnedById = new Map(
    returned.map((listing) => [listing.id, listing] as const)
  );
  const staleIds: string[] = [];
  const reasons = emptyReasons();

  for (const id of savedIds) {
    const listing = returnedById.get(id);
    if (!listing) {
      staleIds.push(id);
      reasons.absent += 1;
      continue;
    }
    if (!ignoreShowOnWebsite && listing.show_on_web === false) {
      staleIds.push(id);
      reasons.hidden += 1;
      continue;
    }
    if (isInactiveStatus(listing.listing_status)) {
      staleIds.push(id);
      reasons.inactive += 1;
    }
  }

  if (
    staleIds.length === savedIds.length &&
    savedIds.length >= MASS_WIPE_MIN_SAVED_IDS
  ) {
    return {
      staleIds: [],
      skippedMassWipe: true,
      reasons,
    };
  }

  return {
    staleIds,
    skippedMassWipe: false,
    reasons,
  };
};
